import { FC, ReactNode, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import styles from './Toast.module.scss'

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

export type ToastProps = {
	isOpen: boolean
	onClose: () => void
	children: ReactNode
	position?: ToastPosition
	duration?: number
	className?: string
}

export const Toast: FC<ToastProps> = ({
	isOpen,
	onClose,
	children,
	position = 'bottom-center',
	duration = 3000,
	className = ''
}) => {
	const [isVisible, setIsVisible] = useState(false)
	const [isMounted, setIsMounted] = useState(false)

	useEffect(() => {
		if (isOpen) {
			setIsMounted(true)
			requestAnimationFrame(() => {
				setIsVisible(true)
			})

			if (duration > 0) {
				const timer = setTimeout(() => {
					handleClose()
				}, duration)
				return () => clearTimeout(timer)
			}
		}
	}, [isOpen, duration])

	const handleClose = () => {
		setIsVisible(false)
		setTimeout(() => {
			setIsMounted(false)
			onClose()
		}, 200)
	}

	if (!isMounted) return null

	return createPortal(
		<div
			className={`${styles.toast} ${styles[position]} ${isVisible ? styles.visible : ''} ${className}`}
			role="alert"
			aria-live="polite">
			<div className={styles.content}>{children}</div>
			<button className={styles.closeButton} onClick={handleClose} aria-label="Закрыть">
				×
			</button>
		</div>,
		document.body
	)
}
